import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ParrotBrandMark from '../components/ParrotBrandMark';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/parrot-speech-improvement/id1457391626';

type OfferPreview = {
  uid?: string;
  expires?: number;
};

function decodeOfferToken(token: string): OfferPreview | null {
  const [encodedPayload] = token.split('.');
  if (!encodedPayload) return null;
  try {
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

const ParrotOffer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  const offerPreview = useMemo(() => decodeOfferToken(token), [token]);
  const hasInvite = Boolean(token && offerPreview?.uid);
  const isExpired = Boolean(offerPreview?.expires && Date.now() > offerPreview.expires);
  const checkoutUrl = token
    ? `/api/create-parrot-offer-checkout?token=${encodeURIComponent(token)}`
    : '';

  return (
    <div className="min-h-screen bg-[#fff5c2] text-parrot-black overflow-hidden">
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-12 lg:px-16 py-6">
        <Link to="/" className="flex items-center gap-3">
          <ParrotBrandMark className="h-11 w-11" />
          <span className="text-sm font-black tracking-[0.28em]">PARROT</span>
        </Link>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer"
          className="rounded-full bg-parrot-black px-4 py-2 text-sm font-bold text-white hover:bg-parrot-blue transition-colors"
        >
          App Store
        </a>
      </header>

      <main className="relative">
        <section className="relative px-6 sm:px-12 lg:px-16 pb-14 pt-4">
          <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.02fr_0.98fr]">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex rounded-full border-2 border-parrot-black bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.22em] shadow-[5px_5px_0_#0E0E0E]">
                Welcome back offer
              </div>
              <h1 className="text-5xl font-black leading-[0.94] tracking-normal text-parrot-black sm:text-6xl lg:text-7xl">
                Parrot got rebuilt for better speakers.
              </h1>
              <p className="mt-5 max-w-2xl text-lg font-semibold leading-8 text-parrot-black/80 sm:text-xl">
                New look, sharper speech feedback, speaking challenges, and friend speak-offs. Come back with one free month and see how much smoother practice feels now.
              </p>

              {checkoutCancelled ? (
                <div className="mt-6 rounded-2xl border-2 border-parrot-black bg-white px-4 py-3 text-sm font-bold shadow-[4px_4px_0_#0E0E0E]">
                  Checkout was cancelled. Your invite is still here when you are ready.
                </div>
              ) : null}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                {hasInvite && !isExpired ? (
                  <a
                    href={checkoutUrl}
                    className="inline-flex items-center justify-center rounded-full bg-parrot-blue px-7 py-4 text-base font-black text-white shadow-[6px_6px_0_#0E0E0E] transition-transform hover:-translate-y-0.5"
                  >
                    Claim your free month
                  </a>
                ) : (
                  <div className="inline-flex items-center justify-center rounded-full bg-parrot-black/10 px-7 py-4 text-base font-black text-parrot-black/60">
                    Open your personal invite
                  </div>
                )}
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-full border-2 border-parrot-black bg-white px-7 py-4 text-base font-black text-parrot-black transition-transform hover:-translate-y-0.5"
                >
                  Download Parrot
                </a>
              </div>

              <p className="mt-5 max-w-xl text-sm font-semibold leading-6 text-parrot-black/65">
                This free month is attached to the account that received the email. After checkout, sign in to Parrot with that same email.
              </p>

              {isExpired ? (
                <p className="mt-4 max-w-xl rounded-2xl border-2 border-parrot-coral bg-white px-4 py-3 text-sm font-bold text-parrot-coral-deep">
                  This invite has expired. Open the latest Parrot email and use the newest offer link.
                </p>
              ) : null}
            </div>

            <div className="relative min-h-[560px]">
              <div className="absolute left-1/2 top-6 h-[470px] w-[270px] -translate-x-1/2 rotate-[-7deg] rounded-[42px] border-[10px] border-parrot-black bg-parrot-yellow shadow-[18px_18px_0_#0E0E0E]">
                <div className="mx-auto mt-4 h-7 w-24 rounded-full bg-parrot-black" />
                <div className="px-6 pt-10">
                  <div className="rounded-3xl bg-white p-5 shadow-[0_8px_0_rgba(14,14,14,0.14)]">
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-parrot-blue">Challenge</div>
                    <div className="mt-3 text-2xl font-black leading-7">Start a 60-second speak-off</div>
                    <div className="mt-5 h-3 rounded-full bg-parrot-black/10">
                      <div className="h-3 w-3/4 rounded-full bg-parrot-coral" />
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <Stat label="Clarity" value="92" />
                    <Stat label="Pace" value="88" />
                  </div>
                  <div className="mt-5 rounded-3xl bg-parrot-blue p-5 text-white">
                    <div className="text-sm font-black">Invite a friend</div>
                    <div className="mt-1 text-xs font-semibold text-white/80">See who gives the stronger answer.</div>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-6 left-4 rounded-3xl border-2 border-parrot-black bg-white px-5 py-4 shadow-[7px_7px_0_#0E0E0E] sm:left-14">
                <div className="text-3xl font-black">1 month</div>
                <div className="text-sm font-bold text-parrot-black/60">free for returning members</div>
              </div>
              <div className="absolute right-0 top-10 hidden rounded-3xl border-2 border-parrot-black bg-parrot-coral px-5 py-4 text-white shadow-[7px_7px_0_#0E0E0E] sm:block">
                <div className="text-sm font-black uppercase tracking-[0.18em]">New UI</div>
                <div className="mt-1 text-lg font-black">Cleaner flow</div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y-2 border-parrot-black bg-white px-6 py-8 sm:px-12 lg:px-16">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
            <Feature title="Sharper feedback" body="Review score details, transcript highlights, and concrete ways to improve the next speech." />
            <Feature title="Speaking challenges" body="Practice with focused prompts that make each timed speaking exercise feel fresh." />
            <Feature title="Friend speak-offs" body="Challenge friends and compare completed speeches in a more social Parrot experience." />
          </div>
        </section>
      </main>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl bg-white p-4 text-center">
    <div className="text-3xl font-black text-parrot-blue">{value}</div>
    <div className="text-xs font-black uppercase tracking-[0.18em] text-parrot-black/50">{label}</div>
  </div>
);

const Feature: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="rounded-2xl border-2 border-parrot-black bg-[#fff5c2] p-5 shadow-[5px_5px_0_#0E0E0E]">
    <h2 className="text-xl font-black">{title}</h2>
    <p className="mt-2 text-sm font-semibold leading-6 text-parrot-black/70">{body}</p>
  </div>
);

export default ParrotOffer;
